import { useEffect, useRef } from 'react';
import styles from './Dragon.module.css';

function Dragon() {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const lerp = (a, b, t) => a + (b - a) * t;
    const cos = Math.cos;
    const sin = Math.sin;
    const radians = (degrees) => (degrees * Math.PI) / 180;
    const atan2 = Math.atan2;

    class DragonFly {
      constructor() {
        this.x = -200; // Começa fora da tela à esquerda
        this.y = canvas.height / 2;
        this.w = 150;
        this.h = 100;
        this.vx = 0;
        this.vy = 0;
        this.segments = [];
        this.timer = 0;
        this.speed = 4;
        this.lift = 3;
        this.showLegs = true;
        this.nodes = [10, 14, 12, 18, 23, 28, 37, 42, 50, 50, 46, 42, 35, 28, 22, 18, 15, 14, 11, 9, 8, 7, 5, 3, 2, 1, 1, 1];
        this.m = 0;
        this.m2 = 0;
        this.diff = 0;
        this.direction = -1; // Começa voando para direita
        this.scaleX = -1;

        this.colors = {
          spikes: '#0a4d1a',
          black: '#1e3a2e',
          body: '#10b981',
          wingArm: '#059669',
          wingArmSpike: '#34d399',
          wingUpper: '#10b981',
          wingLower: '#6ee7b7',
          wingSpines: 'rgba(16, 185, 129, 0.25)',
          legsBehind: '#059669'
        };

        this.wing = {
          x1: 0, y1: 0, x2: 0, y2: 0, x3: 0, y3: 0, x4: 0, y4: 0,
          spines: {
            a: { x1: 0, y1: 0, x2: 0, y2: 0 },
            b: { x1: 0, y1: 0, x2: 0, y2: 0 },
            c: { x1: 0, y1: 0, x2: 0, y2: 0 },
            d: { x1: 0, y1: 0, x2: 0, y2: 0 }
          }
        };

        // Initialize segments
        for (let i = 0; i <= 27; i++) {
          this.segments.push({
            x: this.x - (this.w * i) / 27, // Inverte para começar em linha
            y: this.y
          });
        }
      }

      update() {
        this.diff = this.segments[0].y - this.segments[1].y;

        this.timer += 0.018;
        this.m = sin(this.timer * this.speed) * this.lift;
        this.m2 = cos(this.timer * this.speed) * this.lift;

        // Update each of the segments
        for (let i = 0; i < this.segments.length; i++) {
          if (i === 0) {
            this.segments[i].y += this.m;
            this.segments[i].x = this.x;
          } else {
            this.segments[i].y = lerp(this.segments[i].y, this.segments[i - 1].y, 0.20);
            this.segments[i].x = lerp(this.segments[i].x, this.segments[i - 1].x, 0.20);
          }
        }

        // Move the wing
        this.wing.x1 = this.segments[7].x;
        this.wing.y1 = this.segments[7].y + 10;
        this.wing.x2 = this.segments[7].x + 20;
        this.wing.y2 = this.segments[7].y + 10 + this.m * 15;
        this.wing.x3 = this.segments[7].x - 20;
        this.wing.y3 = this.segments[7].y + 10 + this.m * 30;
        this.wing.x4 = this.segments[7].x - 27;
        this.wing.y4 = this.segments[7].y + 10 + this.m * 35;

        // Wing spines
        this.wing.spines.a.x1 = this.wing.x1;
        this.wing.spines.a.y1 = this.segments[7].y + 40 + this.m * 47 - this.m2 * 6;
        this.wing.spines.a.x2 = this.segments[7].x + 90;
        this.wing.spines.a.y2 = this.segments[7].y + 40 + this.m * 55 - this.m2 * 6;

        this.wing.spines.b.x1 = this.wing.x1 + 20;
        this.wing.spines.b.y1 = this.segments[7].y + 40 + this.m * 42;
        this.wing.spines.b.x2 = this.segments[7].x + 95;
        this.wing.spines.b.y2 = this.segments[7].y + 40 + this.m * 45;

        this.wing.spines.c.x1 = this.wing.x1 + 25;
        this.wing.spines.c.y1 = this.segments[7].y + 40 + this.m * 35;
        this.wing.spines.c.x2 = this.segments[7].x + 100;
        this.wing.spines.c.y2 = this.segments[7].y + 40 + this.m * 32;

        this.wing.spines.d.x1 = this.wing.x1 + 35;
        this.wing.spines.d.y1 = this.segments[7].y + 40 + this.m * 17;
        this.wing.spines.d.x2 = this.segments[7].x + 80;
        this.wing.spines.d.y2 = this.segments[7].y + 30 + this.m * 15;

        this.x -= this.speed * this.direction;

        // Muda de direção quando sai da tela
        if (this.direction === 1 && this.x < -400) {
          this.direction = -1;
          this.scaleX = -1;
        } else if (this.direction === -1 && this.x > canvas.width + 400) {
          this.direction = 1;
          this.scaleX = 1;
        }
      }

      drawFrontLeg(ctx, back) {
        ctx.save();
        ctx.translate(this.segments[8].x, this.segments[8].y);
        
        // Espelha quando voa para direita
        if (this.direction === -1) {
          ctx.scale(-1, 1);
        }
        
        ctx.translate(-225, -340);

        if (back) {
          ctx.translate(-10, 0);
          ctx.fillStyle = this.colors.legsBehind;
        } else {
          ctx.fillStyle = this.colors.body;
        }

        ctx.translate(224 + 11, 355);
        ctx.rotate(radians(this.diff));
        ctx.translate(-224 - 11, -355);

        ctx.beginPath();
        ctx.moveTo(224, 355);
        ctx.bezierCurveTo(226, 355, 230, 357, 233, 363);
        ctx.bezierCurveTo(238, 368, 239, 374, 235, 380);
        ctx.bezierCurveTo(229, 388, 222, 393, 220, 398);
        ctx.bezierCurveTo(221, 402, 223, 404, 228, 408);
        ctx.bezierCurveTo(223, 409, 218, 410, 213, 404);
        ctx.bezierCurveTo(212, 398, 216, 389, 221, 376);
        ctx.bezierCurveTo(209, 364, 204, 355 + this.diff * 0.5, 202, 355 + this.diff * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }

      drawBackLeg(ctx, back) {
        ctx.save();
        ctx.translate(this.segments[11].x, this.segments[11].y);
        
        // Espelha quando voa para direita
        if (this.direction === -1) {
          ctx.scale(-1, 1);
        }
        
        ctx.translate(-278, -342);

        if (back) {
          ctx.translate(-10, 0);
          ctx.fillStyle = this.colors.legsBehind;
        } else {
          ctx.fillStyle = this.colors.body;
        }

        ctx.translate(286 + 17, 354);
        ctx.rotate(radians(this.diff));
        ctx.translate(-286 - 17, -354);

        ctx.beginPath();
        ctx.moveTo(286, 352);
        ctx.bezierCurveTo(289, 356, 291, 365, 290, 376);
        ctx.bezierCurveTo(296, 380, 300, 382, 307, 386);
        ctx.bezierCurveTo(308, 394, 308, 409, 308, 416);
        ctx.bezierCurveTo(309, 418, 312, 419, 316, 416);
        ctx.bezierCurveTo(315, 421, 310, 425, 302, 423);
        ctx.bezierCurveTo(300, 412, 300, 404, 299, 400);
        ctx.bezierCurveTo(294, 395, 286, 394, 276, 391);
        ctx.bezierCurveTo(268, 383, 261, 371, 252, 357);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }

      drawRightWing(ctx) {
        // Wing covering
        if (this.wing.y2 < this.wing.y1) {
          ctx.fillStyle = this.colors.wingUpper;
        } else {
          ctx.fillStyle = this.colors.wingLower;
        }

        ctx.beginPath();
        ctx.moveTo(this.wing.x1, this.wing.y1);
        ctx.bezierCurveTo(
          this.wing.x2 - 10, this.wing.y2,
          this.wing.x2 - 10, this.wing.y2,
          this.wing.x3, this.wing.y3
        );
        ctx.bezierCurveTo(
          this.wing.x3 - 5, this.wing.y3 - this.m2 * 3,
          this.wing.spines.a.x1 - 5, this.wing.spines.a.y1 + this.diff * 0.5 - this.m2 * 3,
          this.wing.spines.a.x1, this.wing.spines.a.y1 - this.m2 * 3
        );
        ctx.bezierCurveTo(
          this.wing.spines.a.x1 + 10, this.wing.spines.a.y1 - this.diff * 0.5,
          this.wing.spines.a.x2 - 10, this.wing.spines.a.y2 + this.diff * 0.5,
          this.wing.spines.a.x2, this.wing.spines.a.y2
        );
        ctx.lineTo(this.wing.spines.a.x2, this.wing.spines.a.y2);
        ctx.bezierCurveTo(
          this.wing.spines.a.x2 - 10, this.wing.spines.a.y2,
          this.wing.spines.b.x2 - 15, this.wing.spines.b.y2 - this.diff * 0.5,
          this.wing.spines.b.x2, this.wing.spines.b.y2
        );
        ctx.bezierCurveTo(
          this.wing.spines.b.x2 - 10, this.wing.spines.b.y2,
          this.wing.spines.c.x2 - 15, this.wing.spines.c.y2,
          this.wing.spines.c.x2, this.wing.spines.c.y2
        );
        ctx.bezierCurveTo(
          this.wing.spines.c.x2 - 10, this.wing.spines.c.y2,
          this.wing.spines.d.x2 - 15, this.wing.spines.d.y2,
          this.wing.spines.d.x2, this.wing.spines.d.y2
        );
        ctx.bezierCurveTo(
          this.wing.spines.d.x2 - 10, this.wing.spines.d.y2,
          this.wing.x1 + 40 - 15, this.segments[9].y + this.nodes[9] * 0.2,
          this.wing.x1 + 40, this.segments[9].y + this.nodes[9] * 0.2
        );
        ctx.closePath();
        ctx.fill();

        // Wing arm
        ctx.strokeStyle = this.colors.wingArm;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(this.wing.x1, this.wing.y1);
        ctx.lineTo(this.wing.x2, this.wing.y2);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.x2, this.wing.y2);
        ctx.lineTo(this.wing.x3, this.wing.y3);
        ctx.stroke();

        // Top of wing arm with small spike
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.bezierCurveTo(
          this.wing.x3 - 3, this.wing.y3 + ((this.wing.y4 - this.wing.y3) * 0.3),
          this.wing.x4 - 2, this.wing.y4 - ((this.wing.y4 - this.wing.y3) * 0.5),
          this.wing.x4, this.wing.y4
        );
        ctx.stroke();

        // Spike on main arm
        ctx.fillStyle = this.colors.wingArmSpike;
        ctx.beginPath();
        ctx.moveTo(this.wing.x4 - 2, this.wing.y4);
        ctx.lineTo(this.wing.x4 + 2, this.wing.y4);
        ctx.lineTo(this.wing.x4, this.wing.y4 + (this.wing.y4 - this.wing.y3));
        ctx.closePath();
        ctx.fill();

        // Wing spines
        ctx.strokeStyle = this.colors.wingSpines;
        ctx.lineWidth = 1;

        // 1st (top) spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.bezierCurveTo(
          this.wing.x3 - 5, this.wing.y3,
          this.wing.spines.a.x1 - 5, this.wing.spines.a.y1 + this.diff * 0.5 - this.m2 * 3,
          this.wing.spines.a.x1, this.wing.spines.a.y1 - this.m2 * 3
        );
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.spines.a.x1, this.wing.spines.a.y1 - this.m2 * 3);
        ctx.bezierCurveTo(
          this.wing.spines.a.x1 + 10, this.wing.spines.a.y1 - this.diff,
          this.wing.spines.a.x2 - 10, this.wing.spines.a.y2 + this.diff * 0.5,
          this.wing.spines.a.x2, this.wing.spines.a.y2
        );
        ctx.stroke();

        // 2nd spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.lineTo(this.wing.spines.b.x1, this.wing.spines.b.y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.spines.b.x1, this.wing.spines.b.y1);
        ctx.lineTo(this.wing.spines.b.x2, this.wing.spines.b.y2);
        ctx.stroke();

        // 3rd spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.lineTo(this.wing.spines.c.x1, this.wing.spines.c.y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.spines.c.x1, this.wing.spines.c.y1);
        ctx.lineTo(this.wing.spines.c.x2, this.wing.spines.c.y2);
        ctx.stroke();

        // 4th spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x2, this.wing.y2);
        ctx.lineTo(this.wing.spines.d.x2, this.wing.spines.d.y2);
        ctx.stroke();
      }

      drawLeftWing(ctx) {
        // Wing covering
        if (this.wing.y2 >= this.wing.y1) {
          ctx.fillStyle = this.colors.wingUpper;
          // Line where wing joins to the body
          ctx.strokeStyle = 'rgba(40, 40, 40, 0.19)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(this.wing.x1, this.wing.y1);
          ctx.lineTo(this.wing.x1 + 40, this.segments[9].y + this.nodes[9] * 0.2);
          ctx.stroke();
        } else {
          ctx.fillStyle = this.colors.wingLower;
        }

        ctx.beginPath();
        ctx.moveTo(this.wing.x1, this.wing.y1);
        ctx.bezierCurveTo(
          this.wing.x2 - 10, this.wing.y2,
          this.wing.x2 - 10, this.wing.y2,
          this.wing.x3, this.wing.y3
        );
        ctx.bezierCurveTo(
          this.wing.x3 - 5, this.wing.y3 - this.m2 * 3,
          this.wing.spines.a.x1 - 5, this.wing.spines.a.y1 + this.diff * 0.5 - this.m2 * 3,
          this.wing.spines.a.x1, this.wing.spines.a.y1 - this.m2 * 3
        );
        ctx.bezierCurveTo(
          this.wing.spines.a.x1 + 10, this.wing.spines.a.y1 - this.diff * 0.5,
          this.wing.spines.a.x2 - 10, this.wing.spines.a.y2 + this.diff * 0.5,
          this.wing.spines.a.x2, this.wing.spines.a.y2
        );
        ctx.lineTo(this.wing.spines.a.x2, this.wing.spines.a.y2);
        ctx.bezierCurveTo(
          this.wing.spines.a.x2 - 10, this.wing.spines.a.y2,
          this.wing.spines.b.x2 - 15, this.wing.spines.b.y2 - this.diff * 0.5,
          this.wing.spines.b.x2, this.wing.spines.b.y2
        );
        ctx.bezierCurveTo(
          this.wing.spines.b.x2 - 10, this.wing.spines.b.y2,
          this.wing.spines.c.x2 - 15, this.wing.spines.c.y2,
          this.wing.spines.c.x2, this.wing.spines.c.y2
        );
        ctx.bezierCurveTo(
          this.wing.spines.c.x2 - 10, this.wing.spines.c.y2,
          this.wing.spines.d.x2 - 15, this.wing.spines.d.y2,
          this.wing.spines.d.x2, this.wing.spines.d.y2
        );
        ctx.bezierCurveTo(
          this.wing.spines.d.x2 - 10, this.wing.spines.d.y2,
          this.wing.x1 + 40 - 15, this.segments[9].y + this.nodes[9] * 0.2,
          this.wing.x1 + 40, this.segments[9].y + this.nodes[9] * 0.2
        );
        ctx.closePath();
        ctx.fill();

        // Wing arm
        ctx.strokeStyle = this.colors.wingArm;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(this.wing.x1, this.wing.y1);
        ctx.lineTo(this.wing.x2, this.wing.y2);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.x2, this.wing.y2);
        ctx.lineTo(this.wing.x3, this.wing.y3);
        ctx.stroke();

        // Top of wing arm with small spike
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.bezierCurveTo(
          this.wing.x3 - 3, this.wing.y3 + ((this.wing.y4 - this.wing.y3) * 0.3),
          this.wing.x4 - 2, this.wing.y4 - ((this.wing.y4 - this.wing.y3) * 0.5),
          this.wing.x4, this.wing.y4
        );
        ctx.stroke();

        // Spike on main arm
        ctx.fillStyle = this.colors.wingArmSpike;
        ctx.beginPath();
        ctx.moveTo(this.wing.x4 - 2, this.wing.y4);
        ctx.lineTo(this.wing.x4 + 2, this.wing.y4);
        ctx.lineTo(this.wing.x4, this.wing.y4 + (this.wing.y4 - this.wing.y3));
        ctx.closePath();
        ctx.fill();

        // Wing spines
        ctx.strokeStyle = this.colors.wingSpines;
        ctx.lineWidth = 1;

        // 1st (top) spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.bezierCurveTo(
          this.wing.x3 - 5, this.wing.y3,
          this.wing.spines.a.x1 - 5, this.wing.spines.a.y1 + this.diff * 0.5 - this.m2 * 3,
          this.wing.spines.a.x1, this.wing.spines.a.y1 - this.m2 * 3
        );
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.spines.a.x1, this.wing.spines.a.y1 - this.m2 * 3);
        ctx.bezierCurveTo(
          this.wing.spines.a.x1 + 10, this.wing.spines.a.y1 - this.diff,
          this.wing.spines.a.x2 - 10, this.wing.spines.a.y2 + this.diff * 0.5,
          this.wing.spines.a.x2, this.wing.spines.a.y2
        );
        ctx.stroke();

        // 2nd spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.lineTo(this.wing.spines.b.x1, this.wing.spines.b.y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.spines.b.x1, this.wing.spines.b.y1);
        ctx.lineTo(this.wing.spines.b.x2, this.wing.spines.b.y2);
        ctx.stroke();

        // 3rd spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x3, this.wing.y3);
        ctx.lineTo(this.wing.spines.c.x1, this.wing.spines.c.y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(this.wing.spines.c.x1, this.wing.spines.c.y1);
        ctx.lineTo(this.wing.spines.c.x2, this.wing.spines.c.y2);
        ctx.stroke();

        // 4th spine
        ctx.beginPath();
        ctx.moveTo(this.wing.x2, this.wing.y2);
        ctx.lineTo(this.wing.spines.d.x2, this.wing.spines.d.y2);
        ctx.stroke();
      }

      drawHead(ctx) {
        ctx.save();
        ctx.translate(this.segments[0].x, this.segments[0].y);
        
        // Espelha a cabeça quando voa para direita (esquerda para direita)
        if (this.direction === -1) {
          ctx.scale(-1, 1);
        }
        
        ctx.rotate(radians(-this.m * 8));
        ctx.translate(-65, -125);
        ctx.scale(0.6, 0.6);

        ctx.fillStyle = this.colors.body;
        ctx.beginPath();
        ctx.moveTo(135, 200);
        ctx.bezierCurveTo(141, 197, 149, 194, 156, 190);
        ctx.bezierCurveTo(153, 196, 153, 204, 156, 208);
        ctx.bezierCurveTo(150, 214, 148, 218, 150, 226);
        ctx.bezierCurveTo(144, 227, 141, 235, 144, 243);
        ctx.bezierCurveTo(142, 244, 136, 246, 135, 252);
        ctx.lineTo(130, 245);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = this.colors.body;
        ctx.beginPath();
        ctx.moveTo(72, 235);
        ctx.lineTo(73, 232);
        ctx.bezierCurveTo(76, 228, 80, 224, 84, 222);
        ctx.bezierCurveTo(90, 220, 94, 218, 98, 215);
        ctx.bezierCurveTo(105, 209, 114, 203, 122, 200);
        ctx.bezierCurveTo(130, 199, 136, 200, 138, 203);
        ctx.bezierCurveTo(138, 206, 140, 209, 140, 211);
        ctx.bezierCurveTo(137, 214, 136, 216, 138, 219);
        ctx.bezierCurveTo(137, 221, 137, 223, 138, 225);
        ctx.bezierCurveTo(136, 226, 134, 228, 136, 232);
        ctx.bezierCurveTo(137, 234, 136, 236, 134, 237);
        ctx.lineTo(136, 240);
        ctx.bezierCurveTo(132, 244, 125, 245, 113, 243);
        ctx.bezierCurveTo(106, 240, 98, 241, 88, 243);
        ctx.bezierCurveTo(80, 244, 74, 238, 72, 235);
        ctx.closePath();
        ctx.fill();

        // Horns
        ctx.fillStyle = this.colors.black;
        ctx.beginPath();
        ctx.moveTo(138, 204);
        ctx.bezierCurveTo(157, 196, 176, 182, 184, 174);
        ctx.bezierCurveTo(172, 194, 155, 205, 138, 211);
        ctx.closePath();
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(137, 221);
        ctx.bezierCurveTo(145, 221, 154, 218, 159, 215);
        ctx.bezierCurveTo(153, 222, 145, 225, 138, 226);
        ctx.closePath();
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(135, 232);
        ctx.bezierCurveTo(138, 234, 142, 234, 149, 236);
        ctx.lineTo(145, 238);
        ctx.lineTo(140, 238);
        ctx.lineTo(134, 237);
        ctx.closePath();
        ctx.fill();

        // Mouth
        ctx.fillStyle = this.colors.black;
        ctx.beginPath();
        ctx.moveTo(73, 236);
        ctx.bezierCurveTo(85, 236, 97, 229, 106, 228);
        ctx.stroke();

        // Eye
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(104, 217);
        ctx.lineTo(107, 214);
        ctx.lineTo(118, 209);
        ctx.bezierCurveTo(116, 214, 111, 217, 104, 217);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = this.colors.black;
        ctx.beginPath();
        ctx.arc(111, 214, 3, 0, Math.PI * 2);
        ctx.fill();

        // Nose
        ctx.fillStyle = this.colors.black;
        ctx.beginPath();
        ctx.moveTo(77, 232);
        ctx.bezierCurveTo(78, 230, 79, 228, 81, 228);
        ctx.bezierCurveTo(81, 230, 79, 232, 76, 232);
        ctx.closePath();
        ctx.fill();

        // Lines across top of head
        ctx.strokeStyle = this.colors.black;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(86, 223);
        ctx.lineTo(89, 226);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(92, 220);
        ctx.lineTo(94, 222);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(98, 217);
        ctx.lineTo(100, 220);
        ctx.stroke();

        // Sockets
        ctx.strokeStyle = 'rgba(56, 55, 54, 0.19)';
        ctx.beginPath();
        ctx.moveTo(123, 205);
        ctx.bezierCurveTo(128, 204, 134, 203, 138, 203);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(128, 214);
        ctx.bezierCurveTo(132, 213, 136, 213, 139, 213);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(130, 219);
        ctx.bezierCurveTo(132, 218, 136, 220, 138, 220);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(130, 225);
        ctx.bezierCurveTo(132, 226, 134, 226, 137, 226);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(126, 228);
        ctx.bezierCurveTo(129, 229, 132, 230, 134, 232);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(124, 232);
        ctx.bezierCurveTo(128, 234, 130, 235, 134, 236);
        ctx.stroke();

        ctx.restore();
      }

      draw(ctx) {
        ctx.save();

        ctx.save();
        ctx.translate(-20, 10);
        this.drawRightWing(ctx);
        ctx.restore();

        if (this.showLegs) {
          this.drawBackLeg(ctx, true);
          this.drawFrontLeg(ctx, true);
        }

        // Spikes on top of body
        ctx.fillStyle = this.colors.spikes;
        for (let i = 2; i < this.segments.length - 5; i++) {
          const segment = this.segments[i];
          const tx = segment.x;
          const ty = segment.y;
          const a = atan2(this.segments[i + 1].y - ty, this.segments[i + 1].x - tx) - Math.PI / 2;

          ctx.beginPath();
          ctx.moveTo(this.segments[i - 1].x, this.segments[i - 1].y + this.nodes[i] * 0.1);
          ctx.lineTo(segment.x, segment.y + this.nodes[i] * 0.1);
          ctx.lineTo(segment.x - 8 + cos(a) * 12, segment.y - this.nodes[i] * 0.4);
          ctx.closePath();
          ctx.fill();
        }

        // Lower part of the body
        ctx.fillStyle = this.colors.black;
        ctx.beginPath();
        ctx.moveTo(this.segments[0].x, this.segments[0].y);
        for (let i = 1; i < this.segments.length; i += 3) {
          ctx.bezierCurveTo(
            this.segments[i].x, this.segments[i].y,
            this.segments[i + 1].x, this.segments[i + 1].y,
            this.segments[i + 2].x, this.segments[i + 2].y
          );
        }
        for (let i = this.segments.length - 1; i >= 1; i -= 3) {
          ctx.bezierCurveTo(
            this.segments[i].x, this.segments[i].y + this.nodes[i],
            this.segments[i - 1].x, this.segments[i - 1].y + this.nodes[i - 1],
            this.segments[i - 2].x, this.segments[i - 2].y + this.nodes[i - 2]
          );
        }
        ctx.lineTo(this.segments[0].x, this.segments[0].y + this.nodes[1]);
        ctx.closePath();
        ctx.fill();

        // Scales on the lower body
        ctx.strokeStyle = 'rgba(40, 40, 40, 0.58)';
        ctx.lineWidth = 0.5;
        for (let i = 1; i < this.segments.length - 10; i++) {
          const segment = this.segments[i];
          ctx.beginPath();
          ctx.moveTo(segment.x, segment.y);
          ctx.bezierCurveTo(
            segment.x - 10, segment.y + this.nodes[i] * 0.3,
            segment.x - 10, segment.y + this.nodes[i] * 0.7,
            segment.x, segment.y + this.nodes[i] * 0.95
          );
          ctx.stroke();
        }

        // Upper body
        ctx.fillStyle = this.colors.body;
        ctx.beginPath();
        ctx.moveTo(this.segments[0].x, this.segments[0].y);
        for (let i = 1; i < this.segments.length; i += 3) {
          ctx.bezierCurveTo(
            this.segments[i].x, this.segments[i].y,
            this.segments[i + 1].x, this.segments[i + 1].y,
            this.segments[i + 2].x, this.segments[i + 2].y
          );
        }
        for (let i = this.segments.length - 1; i >= 1; i -= 3) {
          ctx.bezierCurveTo(
            this.segments[i].x, this.segments[i].y + this.nodes[i] * 0.6,
            this.segments[i - 1].x, this.segments[i - 1].y + this.nodes[i - 1] * 0.6,
            this.segments[i - 2].x, this.segments[i - 2].y + this.nodes[i - 2] * 0.6
          );
        }
        ctx.closePath();
        ctx.fill();

        if (this.showLegs) {
          this.drawFrontLeg(ctx);
          this.drawBackLeg(ctx);
        }

        this.drawLeftWing(ctx);

        // Triangle tail
        ctx.fillStyle = this.colors.body;
        ctx.beginPath();
        ctx.moveTo(
          this.segments[this.segments.length - 2].x - this.diff * 0.1,
          this.segments[this.segments.length - 2].y - 5
        );
        ctx.lineTo(
          this.segments[this.segments.length - 1].x,
          this.segments[this.segments.length - 1].y
        );
        ctx.lineTo(
          this.segments[this.segments.length - 2].x + this.diff * 0.1,
          this.segments[this.segments.length - 2].y + 5
        );
        ctx.bezierCurveTo(
          this.segments[this.segments.length - 2].x + this.diff * 0.1 + 3,
          this.segments[this.segments.length - 2].y + 2,
          this.segments[this.segments.length - 2].x - this.diff * 0.1 + 3,
          this.segments[this.segments.length - 2].y - 2,
          this.segments[this.segments.length - 2].x - this.diff * 0.1,
          this.segments[this.segments.length - 2].y - 5
        );
        ctx.closePath();
        ctx.fill();

        this.drawHead(ctx);

        ctx.restore();
      }

      fly(ctx) {
        this.draw(ctx);
        this.update();
      }
    }

    const dragon = new DragonFly();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dragon.fly(ctx);
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.canvas} />;
}

export default Dragon;
